import { Children, MouseEvent, ReactElement, ReactNode } from 'react';

import { Button, HStack, Menu, MenuButton, MenuItem, MenuList, Portal, rem, Text, VStack } from '@bonusly/design-system';

import { Reveal } from '../../../components/display/reveal';

interface UserNavMenuProps {
	buttonIcon?: ReactElement;
	buttonText: string;
	children: ReactNode;
	isExpanded: boolean;
}

export const UserNavMenu = ({ buttonIcon, buttonText, children, isExpanded }: UserNavMenuProps) => {
	return (
		<Menu placement="right-start">
			<MenuButton
				as={Button}
				variant="ghost"
				colorScheme="secondary"
				size="sm"
				px={rem(10)}
				py={rem(8)}
				h={rem(40)}
				w={isExpanded ? 'auto' : rem(40)}
				justifyContent={isExpanded ? 'flex-start' : 'center'}
				_active={{ bg: 'secondary.bgAlpha' }}
				_focusVisible={{ outline: '2px solid', outlineColor: 'blue.500', borderRadius: '1px' }}
				data-testid="user-nav-menu-button"
			>
				<HStack spacing={rem(12)} justifyContent={isExpanded ? 'flex-start' : 'center'}>
					{buttonIcon}
					<Reveal if={isExpanded}>
						<Text color="color.default" fontSize={100} fontWeight="medium">
							{buttonText}
						</Text>
					</Reveal>
				</HStack>
			</MenuButton>
			<Portal>
				<MenuList py={rem(6)} px={rem(4)}>
					<VStack spacing={rem(4)} align="stretch">
						{Children.toArray(children)
							// NOTE: hide any falsy children (e.g. anything that is undefined or null that the user does not have access to)
							.filter(Boolean)
							.map((child, index) => {
								//  NOTE: this is for keyboard navigation since the `a` with onClick is on the child (UserNavLink) not the MenuItem
								//  e.detail === 0 indicates a keyboard-triggered click (Enter/Space), while e.detail >= 1 indicates a mouse click
								//  For mouse clicks, the anchor's onClick already fired, so we skip to avoid duplicate navigation
								const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
									if (e.detail !== 0) return;
									const link = e.currentTarget.querySelector('a');
									link?.click();
								};

								return (
									<MenuItem
										key={index}
										h={rem(40)}
										p={0}
										_hover={{ bg: 'none', border: 'none' }}
										_focusVisible={{ bg: 'none', border: '2px solid', borderColor: 'blue.500' }}
										onClick={handleClick}
										data-testid={`user-nav-menu-item-${index}`}
									>
										{child}
									</MenuItem>
								);
							})}
					</VStack>
				</MenuList>
			</Portal>
		</Menu>
	);
};
